import Quill from '@capaj/quill'
import loadImage from 'blueimp-load-image'
import { t } from '@lingui/macro'
import React from 'react'

import allowedFileExtensions from '../../../../../shared/allowedFileExtensions'
import { getFileExtension } from '../../../../../shared/fileNameUtils'
import * as ReactQuillNS from '../../../../../types/Quill'
import { notification } from '../notifications/Notifications'
import CustomQuill from './CustomQuill'
import { css } from '@emotion/react'
import { Box } from 'front-end/src/components'
import { brandColors } from 'front-end/../shared/basicPalette'

interface IProps {
  autofocus?: boolean
  uploadFn: (
    fileName: string,
    blob: Blob,
    onUploadProgress?: (number) => void
  ) => any
  onUploadProgress?: (number) => void
  minHeight?: number
  dataTest?: string
}

export const getQuillStyle = (parentClassname: string) => {
  const newClassname = parentClassname ? `.${parentClassname}` : ''
  return css`
    &${newClassname} {
      padding: 4px 0;
      * a {
        color: var(--chakra-colors-linkText);
        &:hover {
          text-decoration: underline;
        }
      }
      img {
        max-width: 100%;
      }
    }
  `
}

class EditorQuill extends React.Component<
  ReactQuillNS.ComponentProps & IProps
> {
  // This has to be outside of render function for some reason
  // otherwise it causing addRange issues https://github.com/quilljs/quill/issues/1940
  // that is why it's written as class component, not functional one
  quillModules: any = {
    imageUploader: {
      upload: (file: File) => {
        if (this.props.readOnly) {
          notification.warning(t`Unable to upload image to readonly element`)
          return Promise.reject()
        }

        const fileSize = file.size
        const maxFileSize = 1 * 1024 * 1024 * 1024 // 1GB
        if (fileSize > maxFileSize) {
          const maxImageSize = '1GB'
          notification.warning(
            t`Image is bigger than maximum of ${maxImageSize}`
          )
          return Promise.reject()
        }

        const fileExtension = getFileExtension(file.name)

        if (
          !allowedFileExtensions.image.includes(fileExtension.toLowerCase())
        ) {
          notification.error(t`File type ${fileExtension} is not supported`)
          return Promise.reject()
        }

        notification.info(t`Image is being processed`)
        return new Promise((resolve) => {
          loadImage(
            file,
            async (img) => {
              if (!img) {
                notification.error(t`Could not load the image`)
              }
              const imageAsCanvas = img as HTMLCanvasElement & {
                msToBlob: () => Blob
              }
              if (imageAsCanvas.msToBlob) {
                // IE 11 doesn't have toBlob method
                const fileBlob = imageAsCanvas.msToBlob()
                resolve(
                  await this.props.uploadFn(
                    file.name,
                    fileBlob,
                    this.props.onUploadProgress
                  )
                )
              } else {
                imageAsCanvas.toBlob(
                  async (fileBlob) => {
                    resolve(
                      await this.props.uploadFn(
                        file.name,
                        fileBlob as Blob,
                        this.props.onUploadProgress
                      )
                    )
                  },
                  file.type,
                  0.95
                )
              }
            },
            { orientation: true, canvas: true }
          )
        })
      }
    },
    imageResize: {
      parchment: Quill.import('parchment')
    }
  }

  render() {
    const {
      onChange,
      minHeight,
      dataTest = 'EditorQuill',
      ...restOfProps
    } = this.props
    return (
      <Box
        css={css`
          .ql-editor {
            min-height: ${minHeight ? `${minHeight}px` : 'auto'};
          }
          .ql-snow {
            &.ql-toolbar,
            &.ql-container {
              background-color: ${brandColors.greyTransparent[7]};
            }
            &.ql-toolbar {
              border-bottom: 1px solid white;
              padding: 8px 6px;
              background-color: rgba(239, 240, 241, 0.93);
            }
          }
        `}
        data-test={dataTest}
      >
        <CustomQuill
          modules={this.quillModules}
          {...restOfProps}
          onChange={(quillContent, delta, source, editor) => {
            // for looop we are only interested in changes that have source user, see https://github.com/zenoamaro/react-quill/issues/259
            if (onChange && source === 'user') {
              return onChange(quillContent, delta, source, editor)
            }
          }}
        />
      </Box>
    )
  }
}

export default EditorQuill
